import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import Icon from "./Icon";
import { Spinner } from "./ui";
import { avisos as apiAvisos } from "../api/endpoints";
import { relative } from "../lib/format";
import {
  burbuja,
  rutaDelAviso,
  sinLeer,
  textoDelAviso,
  tipoDeAviso,
} from "../lib/avisos";

/* =========================================================================
   LA CAMPANA

   Los avisos del tendero —entró un pedido, le escribieron, se canceló algo—
   vivían solo en el teléfono. Sentado en el mostrador, el panel era el único
   sitio donde no se enteraba.

   VA EN LA BARRA Y NO EN UNA SECCIÓN DEL MENÚ. Un aviso sirve mientras es
   nuevo: si hay que ir a buscarlo a una pantalla, para cuando alguien entra ya
   no era un aviso, era historia. En la barra está a la vista desde cualquier
   pantalla, que es lo único que lo hace útil.

   SE REFRESCA PREGUNTANDO, como el chat y la torre, y por el mismo motivo: el
   panel no tiene Echo cableado. Un minuto es suficiente —el pedido nuevo ya
   aparece solo en el tablero— y no castiga al servidor con una pantalla que se
   deja abierta ocho horas.

   TOCAR UN AVISO LO MARCA LEÍDO Y LLEVA AL SITIO. Marcarlo sin llevar a
   ninguna parte obliga a buscar el pedido a mano en el tablero, que es lo que
   el aviso venía a ahorrar.
   ======================================================================== */

const CADA = 60000;

export default function CampanaDeAvisos() {
  const [abierta, setAbierta] = useState(false);
  const caja = useRef(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["avisos"],
    queryFn: apiAvisos.lista,
    refetchInterval: CADA,
  });

  const lista = data?.notifications ?? [];
  /* La cuenta se saca de la lista y no del `unread` del servidor: así, al
     marcar uno leído, la burbuja baja en el acto sin esperar a la vuelta. */
  const pendientes = sinLeer(lista);

  const recargar = () => qc.invalidateQueries({ queryKey: ["avisos"] });

  const leer = useMutation({ mutationFn: apiAvisos.leer, onSuccess: recargar });
  const leerTodos = useMutation({ mutationFn: apiAvisos.leerTodos, onSuccess: recargar });

  /* Cerrar al tocar fuera y con Escape: es un desplegable de la barra, no un
     diálogo, y quedarse abierto tapando la pantalla es lo que más molesta. */
  useEffect(() => {
    if (!abierta) return;

    const fuera = (e) => {
      if (caja.current && !caja.current.contains(e.target)) setAbierta(false);
    };
    const escape = (e) => {
      if (e.key === "Escape") setAbierta(false);
    };

    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", escape);

    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", escape);
    };
  }, [abierta]);

  const tocar = (aviso) => {
    if (!aviso.read) leer.mutate(aviso.notification_id);

    const ruta = rutaDelAviso(aviso);

    if (ruta) {
      setAbierta(false);
      navigate(ruta);
    }
  };

  const marca = burbuja(pendientes);

  return (
    <div className="campana" ref={caja}>
      <button
        className="btn btn-ghost btn-icon"
        onClick={() => setAbierta((v) => !v)}
        aria-label={
          pendientes > 0 ? `Avisos, ${pendientes} sin leer` : "Avisos"
        }
        aria-expanded={abierta}
        title="Avisos"
      >
        <Icon name="bell" size={17} />
        {marca && <span className="campana-burbuja">{marca}</span>}
      </button>

      {abierta && (
        <div className="campana-panel" role="menu">
          <header>
            <b>Avisos</b>
            {pendientes > 0 && (
              <button
                className="btn btn-ghost"
                onClick={() => leerTodos.mutate()}
                disabled={leerTodos.isPending}
              >
                {leerTodos.isPending ? <Spinner size={13} /> : null}
                Marcar todos
              </button>
            )}
          </header>

          <div className="campana-lista">
            {isLoading ? (
              <p className="faint row gap-2" style={{ padding: "var(--sp-4)" }}>
                <Spinner /> Cargando…
              </p>
            ) : lista.length === 0 ? (
              <p className="faint" style={{ padding: "var(--sp-4)" }}>
                No tienes avisos todavía.
              </p>
            ) : (
              lista.map((aviso) => {
                const tipo = tipoDeAviso(aviso);
                const lleva = Boolean(rutaDelAviso(aviso));

                return (
                  <button
                    key={aviso.notification_id}
                    className={`campana-fila ${aviso.read ? "" : "sin-leer"} ${
                      lleva ? "" : "sin-ruta"
                    }`}
                    onClick={() => tocar(aviso)}
                  >
                    <span className={`campana-icono tono-${tipo.tone}`}>
                      <Icon name={tipo.icono} size={15} />
                    </span>

                    <span className="campana-cuerpo">
                      <b>{tipo.titulo}</b>
                      <span className="faint">{textoDelAviso(aviso)}</span>
                    </span>

                    <time className="faint">{relative(aviso.date)}</time>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
